import { useEffect, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { fetchExtensions } from '@/services/api';
import type { Extension } from '@/types';
import { inputClass } from './format';

export type ExtensionOption = { number: string; name: string };

let extensionPromise: Promise<ExtensionOption[]> | null = null;

function extensionName(extension: Extension) {
  return (
    extension.display_name ||
    extension.caller_id_name ||
    [extension.first_name, extension.last_name].filter(Boolean).join(' ') ||
    extension.username ||
    extension.number
  );
}

/** The PBX's extensions (cached), for owners and assignees; [] when they cannot be read. */
export function useExtensions() {
  const [options, setOptions] = useState<ExtensionOption[] | null>(null);
  useEffect(() => {
    extensionPromise ??= fetchExtensions(true)
      .then((list) =>
        list
          .map((extension) => ({ number: extension.number, name: extensionName(extension) }))
          .sort((a, b) => a.number.localeCompare(b.number, undefined, { numeric: true }))
      )
      .catch(() => {
        extensionPromise = null;
        return [];
      });
    let cancelled = false;
    void extensionPromise.then((list) => {
      if (!cancelled) setOptions(list);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return options;
}

/** Pick an extension from the PBX, or type one when the list is not available. */
export function ExtensionPicker({
  value,
  onChange,
  placeholder = 'Unassigned',
  id,
}: {
  value?: string;
  onChange: (extension: string, name?: string) => void;
  placeholder?: string;
  id?: string;
}) {
  const options = useExtensions();
  if (options && options.length > 0) {
    return (
      <select
        id={id}
        value={value ?? ''}
        onChange={(event) => {
          const option = options.find((item) => item.number === event.target.value);
          onChange(event.target.value, option?.name);
        }}
        className={inputClass}
      >
        <option value="">{placeholder}</option>
        {value && !options.some((option) => option.number === value) && <option value={value}>{value}</option>}
        {options.map((option) => (
          <option key={option.number} value={option.number}>
            {option.number} · {option.name}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      id={id}
      value={value ?? ''}
      onChange={(event) => onChange(event.target.value.replace(/\D/g, ''))}
      placeholder={options === null ? 'Loading extensions…' : 'Extension number'}
      inputMode="numeric"
      className={inputClass}
    />
  );
}

export function TagInput({ value, onChange, suggestions = [] }: { value: string[]; onChange: (tags: string[]) => void; suggestions?: string[] }) {
  const [draft, setDraft] = useState('');
  const add = (raw: string) => {
    const tag = raw.trim().replace(/,$/, '').trim();
    if (!tag || value.some((existing) => existing.toLowerCase() === tag.toLowerCase())) return;
    onChange([...value, tag].slice(0, 20));
  };
  const listId = `tag-suggestions-${suggestions.length}`;
  return (
    <div className="flex flex-wrap items-center gap-1.5 px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus-within:ring-2 focus-within:ring-primary-500">
      {value.map((tag) => (
        <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-50 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 text-xs">
          {tag}
          <button type="button" onClick={() => onChange(value.filter((item) => item !== tag))} aria-label={`Remove ${tag}`}>
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <input
        value={draft}
        list={suggestions.length ? listId : undefined}
        onChange={(event) => {
          if (event.target.value.endsWith(',')) {
            add(event.target.value);
            setDraft('');
          } else setDraft(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            add(draft);
            setDraft('');
          } else if (event.key === 'Backspace' && !draft && value.length) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={() => {
          add(draft);
          setDraft('');
        }}
        placeholder={value.length ? '' : 'Add tags (Enter or comma)'}
        className="flex-1 min-w-[8rem] bg-transparent text-sm text-gray-900 dark:text-white placeholder-gray-400 outline-none py-0.5"
      />
      {suggestions.length > 0 && (
        <datalist id={listId}>
          {suggestions.map((tag) => (
            <option key={tag} value={tag} />
          ))}
        </datalist>
      )}
    </div>
  );
}

export function Field({ label, children, hint, htmlFor }: { label: string; children: ReactNode; hint?: string; htmlFor?: string }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
    </div>
  );
}

/** Switch styled as a toggle, with a label and description. */
export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled,
  badge,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
  badge?: ReactNode;
}) {
  return (
    <label className={`flex items-start gap-3 ${disabled ? 'opacity-60' : 'cursor-pointer'}`}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative mt-0.5 inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${checked ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'}`}
      >
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
      </button>
      <span className="min-w-0">
        <span className="text-sm font-medium text-gray-900 dark:text-white">
          {label} {badge}
        </span>
        {description && <span className="block text-xs text-gray-500 dark:text-gray-400">{description}</span>}
      </span>
    </label>
  );
}
